<template>
  <div class="relative size-full overflow-hidden">
    <UIBaseTransition>
      <div
        v-if="props.loading"
        class="bg-white rounded-xl shadow-lg flex items-center justify-center loader-position w-10 h-10 absolute z-50"
      >
        <UIProgressSpinner
          width="24"
          height="24"
        />
      </div>
    </UIBaseTransition>

    <!-- Legend stays outside the map's LTR pin below — it's regular localizable UI (status
         labels) positioned with logical `start`/`end`, so it must follow the page's direction. -->
    <MapStatusLegend
      :selected-status="selectedStatusFilter"
      @update:selected-status="handleStatusFilterChange"
    />

    <!-- Map canvas ONLY: Google Maps renders its own controls/labels physically LTR and never
         mirrors under RTL — pin just this element so the map tiles, marker positions and
         built-in controls stay untouched, without forcing direction on sibling UI (the legend
         above, the popups below). -->
    <div
      class="size-full"
      dir="ltr"
    >
      <GoogleMap
        ref="mapRef"
        :center="center"
        :api-key="apiKey"
        :zoom="initialZoom"
        class="h-[calc(100%+38px)] select-none"
        language="en"
        version="weekly"
        :libraries="[]"
        disable-default-ui
        gesture-handling="greedy"
        @bounds_changed="handleBoundsChanged"
        @zoom_changed="handleZoomChanged"
      >
        <!-- mapObject only becomes truthy AFTER the Google Maps SDK loads
             (mapRef.value.ready). Without this gate, the Marker :options
             binding evaluates synchronously and calls `new google.maps.Size(...)`
             in getMarkerOptions before `google` is defined on window — the
             whole map fails to mount with "google is not defined". -->
        <template v-if="mapObject">
          <Marker
            v-if="centerByMarker"
            :options="
              getMarkerOptions({
                lat: Number(centerByMarker.lat),
                lng: Number(centerByMarker.lng),
              })
            "
          />
          <Marker
            v-for="(poi, idx) in props.pointsOfInterest"
            :key="`poi-${idx}-${poi.title}`"
            :options="getPoiMarkerOptions(poi)"
          />
        </template>
      </GoogleMap>
    </div>

    <Teleport to="body">
      <div
        v-if="selectedProperty || isPopupLoading"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        @click.self="closePropertyPopup"
      >
        <div
          class="absolute inset-0 bg-black/10"
          @click="closePropertyPopup"
          style="backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px)"
        ></div>
        <div
          class="relative max-w-sm w-full bg-bg rounded-xl shadow-xl overflow-hidden p-2 z-10"
          @click.stop
        >
          <div
            v-if="isPopupLoading"
            class="flex items-center justify-center min-h-[400px]"
          >
            <div class="w-12 h-12 border-4 border-[#FFB648] border-t-transparent rounded-full animate-spin"></div>
          </div>
          <template v-else-if="selectedProperty">
            <div class="relative h-[200px] overflow-hidden rounded-xl mb-2">
              <img
                v-if="selectedProperty?.photo"
                :src="selectedPropertyPhotoUrl"
                :alt="selectedProperty?.title?.en || t('listings.map.propertyAlt')"
                :class="[
                  'size-full rounded-xl',
                  selectedPropertyPhotoUrl === COMING_SOON_PLACEHOLDER ? 'object-cover' : 'object-fill',
                ]"
              />
              <img
                v-else
                src="/images/comingsoon.svg"
                :alt="t('listings.map.propertyAlt')"
                class="size-full object-cover rounded-xl"
              />

              <div
                v-if="selectedProperty?.sales_status?.en"
                class="absolute top-1 end-1"
              >
                <div class="text-caption-1 rounded-lg bg-white px-3 py-2 text-black-100">
                  {{ selectedProperty?.sales_status?.en }}
                </div>
              </div>
            </div>

            <div class="bg-white rounded-xl p-2">
              <div class="mb-3 flex items-center justify-between">
                <div class="text-lg font-bold text-black-100">
                  {{ selectedProperty?.title?.en }}
                </div>
                <div class="flex gap-2">
                  <span
                    v-for="(unit, index) in formattedUnits"
                    :key="index"
                    class="rounded-lg px-2 py-1 text-xs text-black-100 bg-black-05 border border-black-10"
                  >
                    {{ unit.shortRoomType }}
                  </span>
                </div>
              </div>

              <div
                v-if="Number(selectedProperty?.price?.min) > 0"
                class="flex justify-between items-center mb-1 pb-3 border-b border-black-10"
              >
                <span class="text-sm text-black-60">{{ t('listings.map.priceFromLabel') }}</span>
                <span class="text-sm font-bold text-black-100">{{ formattedMinPrice }} AED</span>
              </div>

              <span class="text-sm text-black-100">{{ selectedProperty?.address || '' }}</span>
              <span
                v-if="selectedProperty?.exclusive?.isExclusive"
                class="mt-1 block text-xs font-semibold text-black-100"
              >
                {{ t('exclusives.card.label') }}
              </span>

              <button
                @click="navigateToProperty"
                class="view-project-button"
              >
                {{ t('listings.map.viewDetails') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount } from 'vue';
import groupBy from 'lodash/groupBy';
import { escapeHtml } from '~/utils/escapeHtml';
import { GoogleMap, Marker } from 'vue3-google-map';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';
import type { Filters, Property } from '~/types/properties';
import { nameToColorPropertyMap, getPropertyUnits } from '~/helpers/properties';
import { useAddToFavorites, useRemoveFromFavorites, useIsPropertyFavorited } from '~/api/queries/favorites';
import { checkComingSoonImage, COMING_SOON_PLACEHOLDER } from '~/utils/comingSoonImages';
import MapStatusLegend from '~/components/properties/MapStatusLegend.vue';
import { PROPERTIES_DICTIONARY_KEYS } from '@/api/queries/dictionaries';
import crownBackgroundSvg from '~/assets/icons/crown-background-gold.svg?raw';
import starFilledSvg from '~/assets/icons/star-filled.svg?raw';

const emit = defineEmits(['updated:position', 'update:filters', 'select-property', 'select-position']);

const { t } = useI18n();
const { currentLocale } = useLocaleSwitch();
const { formatNumber } = useFormat();

const runtimeConfig = useRuntimeConfig();
const router = useRouter();
const route = useRoute();

const apiKey = computed(() => runtimeConfig.public.googleMapsApiKey);

type PointOfInterestMarker = {
  lat: number;
  lng: number;
  title: string;
};

type Props = {
  properties?: Property[];
  filters?: Filters;
  zoom?: number;
  centerByMarker?: google.maps.LatLngLiteral;
  defaultMarker?: boolean;
  showControls?: boolean;
  showPropertyPopover?: boolean;
  defaultCenter?: boolean;
  loading?: boolean;
  pointsOfInterest?: PointOfInterestMarker[];
  // Optional focus target — when set the map pans + zooms in on the given
  // coordinate. Used by the project-detail Location tab to "fly" the map
  // to whichever POI the visitor clicks in the right-side list.
  focusedPoi?: { lat: number; lng: number } | null;
  // complex_id of the project whose /explore card is hovered — its marker
  // bounces + rises so the user can locate it on the map.
  highlightedId?: string | null;
  // When true, clicking a marker emits `select-property` (complex_id) instead
  // of opening the built-in popover — /explore uses this to surface the
  // project's card in the left list (which has the photo the lite pins lack).
  cardLinkMode?: boolean;
  // complex_id of the marker whose card is shown anchored over the map (used
  // in fullscreen mode). The map emits `select-position` {x,y} container
  // pixels for it on every pan/zoom so the overlay can sit above the pin.
  selectedId?: string | null;
};

const props = withDefaults(defineProps<Props>(), {
  filters: undefined,
  zoom: 10,
  centerByMarker: undefined,
  showControls: true,
  loading: false,
  showPropertyPopover: false,
  properties: () => [],
  pointsOfInterest: () => [],
  focusedPoi: null,
  highlightedId: null,
  cardLinkMode: false,
  selectedId: null,
});

const { data: salesStatuses } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.SALE_STATUSES);

const getStatusLabelFromId = (statusId: number): string | null => {
  if (!salesStatuses.value) return null;
  const status = salesStatuses.value.find((s: any) => s.id === statusId);
  if (!status) return null;
  return getSalesStatusDisplay(status.name || status.code);
};

const getStatusIdFromLabel = (statusLabel: string): number | null => {
  if (!salesStatuses.value) return null;
  const status = salesStatuses.value.find((s: any) => {
    const displayName = getSalesStatusDisplay(s.name || s.code);
    return displayName === statusLabel;
  });
  return status?.id || null;
};

const selectedStatusFilter = computed(() => {
  if (!props.filters?.sale_status || props.filters.sale_status.length === 0) return null;
  if (props.filters.sale_status.length === 1) {
    return getStatusLabelFromId(props.filters.sale_status[0]) || null;
  }
  return null;
});

const handleStatusFilterChange = (statusLabel: string | null) => {
  if (!props.filters) return;

  const updatedFilters = { ...props.filters };

  if (statusLabel === null) {
    updatedFilters.sale_status = [];
  } else {
    const statusId = getStatusIdFromLabel(statusLabel);
    if (statusId !== null) {
      updatedFilters.sale_status = [statusId];
    }
  }

  emit('update:filters', updatedFilters);
};

const isPropertiesVisible = ref(false);
const selectedProperty = ref<Property | null>(null);
const selectedPropertyPhotoUrl = ref<string>(COMING_SOON_PLACEHOLDER);
const isPropertyFavorite = ref(false);
const isFavoriteLoading = ref(false);
const isPopupLoading = ref(false);
const propertyMarkers = ref<{ [key: string]: google.maps.Marker }>({});
const hoverInfoWindow = ref<google.maps.InfoWindow | null>(null);
const markerClusterer = ref<MarkerClusterer | null>(null);
const isUpdatingMarkers = ref(false);
const lastBounds = ref<{ ne: google.maps.LatLng; sw: google.maps.LatLng } | null>(null);
const eventListeners: google.maps.MapsEventListener[] = [];
const containerClickListeners: Array<{ element: Element; listener: EventListener }> = [];
const markerClickListeners: Map<string, google.maps.MapsEventListener> = new Map();
let propertiesWatchTimeout: NodeJS.Timeout | null = null;
const lastPropertiesLength = ref(0);
const lastExclusiveFilter = ref<boolean | undefined>(undefined);

const VISIBLE_EMIRATES_ZOOM = 10;
const CLUSTERING_DISABLE_ZOOM = 13;
const CLUSTERING_MIN_PROPERTIES = 100;
const HIDE_CITY_NAMES_ZOOM = 11;

const DUBAI_COORDINATES = { lat: 25.2048, lng: 55.2708 };

// `name` is the raw English emirate name — it MUST stay untranslated: it's
// matched against `property.emirate` (groupPropertiesByEmirates below) and
// used to build the InfoWindow DOM element id. Display text is looked up
// separately via `emirateLabel()` (EMIRATE_LABEL_KEYS), which is the only
// place a translation is rendered.
const EMIRATES_COORDINATES = [
  {
    name: 'Abu Dhabi',
    latitude: 24.4539,
    longitude: 54.3773,
  },
  {
    name: 'Dubai',
    latitude: DUBAI_COORDINATES.lat,
    longitude: DUBAI_COORDINATES.lng,
  },
  {
    name: 'Sharjah',
    latitude: 25.3463,
    longitude: 55.4209,
  },
  {
    name: 'Ajman',
    latitude: 25.4052,
    longitude: 55.5136,
  },
  {
    name: 'Umm Al Quwain',
    latitude: 25.5869,
    longitude: 55.5653,
  },
  {
    name: 'Fujairah',
    latitude: 25.1194,
    longitude: 56.3439,
  },
  {
    name: 'Ras Al Khaimah',
    latitude: 25.8007,
    longitude: 55.9762,
  },
];

const EMIRATE_LABEL_KEYS: Record<string, string> = {
  'Abu Dhabi': 'listings.map.emirates.abuDhabi',
  Dubai: 'listings.map.emirates.dubai',
  Sharjah: 'listings.map.emirates.sharjah',
  Ajman: 'listings.map.emirates.ajman',
  'Umm Al Quwain': 'listings.map.emirates.ummAlQuwain',
  Fujairah: 'listings.map.emirates.fujairah',
  'Ras Al Khaimah': 'listings.map.emirates.rasAlKhaimah',
};

const emirateLabel = (name: string): string => {
  const key = EMIRATE_LABEL_KEYS[name];
  return key ? t(key) : name;
};

type EmirateInfoWindow = {
  name: string;
  id: string;
  infoWindow: google.maps.InfoWindow;
};

const emiratesInfoWindow = ref<EmirateInfoWindow[]>([]);

const displayEmirates = () => {
  if (!mapObject.value) return;

  const groupNames = Object.keys(groupPropertiesByEmirates(props.properties));

  EMIRATES_COORDINATES.forEach((emirate) => {
    const isEmirateInfoWindowExists = emiratesInfoWindow.value.find(({ name }) => name === emirate.name);

    if (isEmirateInfoWindowExists) return;

    const id = `emirate-${emirate.name.replaceAll(' ', '-').toLowerCase()}`;

    const content = `
      <div id="${id}" class="select-none">
        <style>
          button.gm-ui-hover-effect, .gm-style-iw-tc:after {
            display: none !important;
          }

          .gm-style-iw {
            box-shadow: none !important;
            border-radius: 0 !important;
            background: none !important;
          }

          .gm-style-iw, .gm-style-iw-ch, .gm-style-iw-d {
            padding: 0 !important;
            overflow: auto !important;
          }
        </style>
        <div class="flex items-center cursor-pointer bg-white gap-2 p-2 shadow-down border border-white rounded-lg hover:border-primary-50 transition-colors duration-300">
          <div class="size-8 rounded-lg bg-black-10 flex items-center justify-center">
            <img src="/oae-flag.webp" width="24" height="16" alt="emirate" />
          </div>
          <span class="text-subhead-2">${escapeHtml(emirateLabel(emirate.name))}</span>
        </div>
      </div>
    `;

    if (groupNames.includes(emirate.name)) {
      const infoWindow = new google.maps.InfoWindow({
        content,
        position: { lat: emirate.latitude, lng: emirate.longitude },
        disableAutoPan: false,
      });

      infoWindow.open(mapObject.value);

      emiratesInfoWindow.value.push({
        id,
        name: emirate.name,
        infoWindow,
      });

      nextTick(() => {
        const emirateDiv = document.querySelector(`#${id}`);

        if (!emirateDiv) return;

        emirateDiv.addEventListener('click', () => {
          if (!mapObject.value) return;

          mapObject.value.setCenter({ lat: emirate.latitude, lng: emirate.longitude });
          mapObject.value.setZoom(12);
        });
      });
    }
  });
};

const hideEmirates = () => {
  emiratesInfoWindow.value.forEach(({ infoWindow }) => {
    infoWindow.close();
  });

  emiratesInfoWindow.value = [];
};

const mapRef = ref<InstanceType<typeof GoogleMap> | null>(null);

const mapObject = computed(() => {
  if (!mapRef.value?.ready) return null;

  return mapRef.value.map;
});

const handleMapChange = () => {
  if (!mapObject.value) return;

  const zoom = mapObject.value.getZoom();

  if (!zoom) return;

  isPropertiesVisible.value = zoom > VISIBLE_EMIRATES_ZOOM;

  if (isPropertiesVisible.value) {
    hideEmirates();
  } else {
    displayEmirates();
  }

  updateMapStyles(zoom);
};

const groupPropertiesByEmirates = (properties: Props['properties']) => {
  if (!properties?.length) return {};

  return groupBy(
    properties.filter((property) => property.emirate),
    (property) => property.emirate,
  );
};

let positionUpdateTimeout: NodeJS.Timeout | null = null;
let isProgrammaticChange = false;

const handleMapPositionChange = () => {
  if (!mapObject.value || isProgrammaticChange) return;

  const bounds = mapObject.value.getBounds();
  const currentZoom = mapObject.value.getZoom();

  if (!bounds || !currentZoom) return;

  if (positionUpdateTimeout) {
    clearTimeout(positionUpdateTimeout);
    positionUpdateTimeout = null;
  }

  positionUpdateTimeout = setTimeout(() => {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const newBounds = {
      ne: new google.maps.LatLng(ne.lat(), ne.lng()),
      sw: new google.maps.LatLng(sw.lat(), sw.lng()),
    };

    const boundsChanged =
      !lastBounds.value ||
      Math.abs(lastBounds.value.ne.lat() - newBounds.ne.lat()) > 0.05 ||
      Math.abs(lastBounds.value.ne.lng() - newBounds.ne.lng()) > 0.05 ||
      Math.abs(lastBounds.value.sw.lat() - newBounds.sw.lat()) > 0.05 ||
      Math.abs(lastBounds.value.sw.lng() - newBounds.sw.lng()) > 0.05;

    if (boundsChanged) {
      lastBounds.value = newBounds;

      emit('updated:position', {
        topLeftLat: ne.lat(),
        topLeftLng: sw.lng(),
        bottomRightLat: sw.lat(),
        bottomRightLng: ne.lng(),
        zoom: currentZoom,
      });
    }

    handleMapChange();
    positionUpdateTimeout = null;
  }, 1000);
};

const handleBoundsChanged = () => {
  handleMapPositionChange();
};

const handleZoomChanged = () => {
  if (mapObject.value) {
    const zoom = mapObject.value.getZoom();
    if (zoom) {
      updateMapStyles(zoom);
    }
  }
  handleMapPositionChange();
};

const calculateCenter = (filters: {
  top_left_lat: number;
  top_left_lon: number;
  bottom_right_lat: number;
  bottom_right_lon: number;
}) => {
  if (!filters) return { lat: 25.2048, lng: 55.2708 };

  const avgLat = (Number(filters.top_left_lat) + Number(filters.bottom_right_lat)) / 2;
  const avgLng = (Number(filters.top_left_lon) + Number(filters.bottom_right_lon)) / 2;

  return { lat: avgLat, lng: avgLng };
};

const calculateMapCenter = () => {
  const isFiltersPresent =
    props.filters?.top_left_lat &&
    props.filters?.top_left_lon &&
    props.filters?.bottom_right_lat &&
    props.filters?.bottom_right_lon;

  if (isFiltersPresent) {
    return calculateCenter({
      top_left_lat: Number(props.filters.top_left_lat),
      top_left_lon: Number(props.filters.top_left_lon),
      bottom_right_lat: Number(props.filters.bottom_right_lat),
      bottom_right_lon: Number(props.filters.bottom_right_lon),
    });
  }

  if (props.centerByMarker) {
    return props.centerByMarker;
  }

  return DUBAI_COORDINATES;
};

const initialZoom = computed(() => {
  if (props.filters?.zoom) {
    return Number(props.filters.zoom);
  }
  return props.zoom;
});

const center = ref(calculateMapCenter());

const infoWindow = ref<google.maps.InfoWindow | null>(null);

// Pan + zoom the map onto whatever the caller passes as `focusedPoi`. Used
// by the Location tab to "fly" the map to a POI clicked in the list. The
// watcher fires only when the SDK is ready (mapObject is non-null) and the
// target actually changed, so we don't fight the user's own pans/zooms.
watch(
  () => props.focusedPoi,
  (next) => {
    if (!next || !mapObject.value) return;
    if (!Number.isFinite(next.lat) || !Number.isFinite(next.lng)) return;
    try {
      mapObject.value.panTo({ lat: next.lat, lng: next.lng });
      const current = mapObject.value.getZoom() ?? 14;
      if (current < 16) mapObject.value.setZoom(16);
    } catch {
      // Map can be torn down between watch fire and call — ignore silently.
    }
  },
);

const addControls = () => {
  if (!mapRef.value || !mapObject.value) return;

  if (!props.showControls) return;

  addControl(mapObject.value, 'zoom', google.maps.ControlPosition.LEFT_TOP);
  addControl(mapObject.value, 'center', google.maps.ControlPosition.LEFT_TOP);
  addControl(mapObject.value, 'mapType', google.maps.ControlPosition.LEFT_TOP);

  infoWindow.value = new google.maps.InfoWindow();
};

const updateMapStyles = (zoom?: number) => {
  if (!mapObject.value) return;

  const currentZoom = zoom || mapObject.value.getZoom() || 10;
  const hideCityNames = currentZoom < HIDE_CITY_NAMES_ZOOM;

  const mapStyles: google.maps.MapTypeStyle[] = [
    {
      featureType: 'poi',
      elementType: 'all',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.business',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.attraction',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.place_of_worship',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.school',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.sports_complex',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.park',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.medical',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.government',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit',
      elementType: 'all',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit.station',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit.line',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit.station.airport',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit.station.bus',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit.station.rail',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'road',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels',
      stylers: [{ visibility: hideCityNames ? 'off' : 'simplified' }],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{ visibility: hideCityNames ? 'simplified' : 'on' }],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ visibility: hideCityNames ? 'simplified' : 'on' }],
    },
    {
      featureType: 'administrative',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels',
      stylers: [{ visibility: hideCityNames ? 'off' : 'on' }],
    },
    {
      featureType: 'administrative.neighborhood',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'water',
      elementType: 'labels',
      stylers: [{ visibility: hideCityNames ? 'off' : 'simplified' }],
    },
    {
      featureType: 'landscape',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ];

  mapObject.value.setOptions({
    styles: mapStyles,
  });
};

watch([mapObject], () => {
  if (mapObject.value) {
    updateMapStyles();
    addControls();

    hoverInfoWindow.value = new google.maps.InfoWindow({
      disableAutoPan: false,
      pixelOffset: new google.maps.Size(0, -20),
    });

    if (hoverInfoWindow.value) {
      const domReadyListener = google.maps.event.addListener(hoverInfoWindow.value, 'domready', () => {
        const closeButtons = document.querySelectorAll(
          '.gm-style-iw-c button, .gm-style-iw-tc button, button[aria-label*="Close"], button[aria-label*="close"]',
        );
        closeButtons.forEach((button) => {
          (button as HTMLElement).style.display = 'none';
        });
        const container = document.querySelector('.gm-style-iw-d');
        if (container) {
          const clickListener = (e: Event) => {
            if (e.target === container) {
              const currentProperty = Object.values(propertyMarkers.value).find(
                (marker) => marker.getVisible() && marker.getMap() === mapObject.value,
              );
              if (currentProperty) {
                const propertyId = currentProperty.get('propertyId');
                if (propertyId) {
                  window.location.href = window.location.origin + '/properties/' + propertyId;
                }
              }
            }
          };
          container.addEventListener('click', clickListener);
          containerClickListeners.push({ element: container, listener: clickListener });
        }
      });
      eventListeners.push(domReadyListener);
    }

    if (!markerClusterer.value && mapObject.value) {
      const gridAlgorithm = new GridAlgorithm({ gridSize: 60 });
      markerClusterer.value = new MarkerClusterer({
        map: mapObject.value,
        markers: [],
        algorithm: gridAlgorithm,
        renderer: {
          render: ({ count, position }) => {
            const color = count > 50 ? '#FF6B6B' : count > 20 ? '#FFB648' : '#4ECDC4';
            const svg =
              '<svg width="80" height="80" xmlns="http://www.w3.org/2000/svg"><circle cx="40" cy="40" r="36" fill="' +
              color +
              '"/><text x="40" y="52" text-anchor="middle" fill="white" font-size="24" font-weight="bold" font-family="Arial, sans-serif">' +
              count +
              '</text></svg>';
            const iconUrl = 'data:image/svg+xml;base64,' + btoa(svg);

            return new google.maps.Marker({
              position,
              icon: {
                url: iconUrl,
                size: new google.maps.Size(80, 80),
                scaledSize: new google.maps.Size(40, 40),
                anchor: new google.maps.Point(20, 20),
              },
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
              optimized: false,
              clickable: true,
            });
          },
        },
      });
    }

    if (!selectionProjection.value) {
      const overlay = new google.maps.OverlayView();
      overlay.onAdd = () => {};
      overlay.onRemove = () => {};
      // draw() runs on every pan/zoom — recompute the anchored overlay here so
      // the selected card tracks its pin.
      overlay.draw = () => emitSelectedPinPosition();
      overlay.setMap(mapObject.value);
      selectionProjection.value = overlay;
    }

    createPropertyMarkers();
  }
});

// ── Hover-to-locate: a dedicated highlight marker ─────────────────────────
// We can't rely on the per-project markers in propertyMarkers — at the
// default zoom they're swallowed by the clusterer (getMap() === null) and a
// first-page card's project is often outside the current viewport (so no
// marker was created at all). Instead we render ONE standalone gold pin at
// the hovered project's coords, added straight to the map (bypassing the
// clusterer), and gently pan to it when it's off-screen. Coords come from
// the pin data already in `props.properties` (keyed by complex_id).
const highlightMarker = ref<google.maps.Marker | null>(null);

const HIGHLIGHT_ICON_URL =
  'data:image/svg+xml;base64,' +
  btoa(
    '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">' +
      '<path d="M22 0C9.85 0 0 9.85 0 22c0 15.4 22 34 22 34s22-18.6 22-34C44 9.85 34.15 0 22 0z" fill="#FFB648" stroke="#0d0f15" stroke-width="2.5"/>' +
      '<circle cx="22" cy="22" r="8" fill="#0d0f15"/></svg>',
  );

const findPinById = (id: string): Property | undefined =>
  (props.properties || []).find((p) => String((p as any).complex_id ?? (p as any).id ?? '') === id);

const clearHighlightMarker = () => {
  highlightMarker.value?.setMap(null);
};

watch(
  () => props.highlightedId,
  (id) => {
    if (typeof google === 'undefined' || !mapObject.value) return;
    if (!id) {
      clearHighlightMarker();
      return;
    }
    const pin = findPinById(id);
    const lat = Number((pin as any)?.latitude);
    const lng = Number((pin as any)?.longitude);
    if (!pin || !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
      clearHighlightMarker();
      return;
    }
    const pos = { lat, lng };
    if (!highlightMarker.value) {
      highlightMarker.value = new google.maps.Marker({
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + 5,
        icon: {
          url: HIGHLIGHT_ICON_URL,
          scaledSize: new google.maps.Size(40, 51),
          anchor: new google.maps.Point(20, 51),
        },
        optimized: false,
        clickable: false,
      });
    }
    highlightMarker.value.setPosition(pos);
    highlightMarker.value.setMap(mapObject.value);
    highlightMarker.value.setAnimation(google.maps.Animation.BOUNCE);
    // Pan into view only when off-screen — avoids jitter for visible pins.
    const bounds = mapObject.value.getBounds();
    if (bounds && !bounds.contains(new google.maps.LatLng(lat, lng))) {
      mapObject.value.panTo(pos);
    }
  },
);

// ── Anchor the selected-card overlay above its pin (fullscreen mode) ───────
// A hidden OverlayView gives us a projection (lat/lng → container pixels).
// Its draw() fires on every pan/zoom, so we recompute + emit the selected
// pin's pixel position continuously and the overlay tracks the marker.
const selectionProjection = ref<google.maps.OverlayView | null>(null);
const emitSelectedPinPosition = () => {
  if (!mapObject.value || !selectionProjection.value || typeof google === 'undefined') {
    emit('select-position', null);
    return;
  }
  const id = props.selectedId;
  if (!id) {
    emit('select-position', null);
    return;
  }
  const pin = (props.properties || []).find((p) => String((p as any).complex_id ?? (p as any).id ?? '') === id);
  const lat = Number((pin as any)?.latitude);
  const lng = Number((pin as any)?.longitude);
  if (!pin || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    emit('select-position', null);
    return;
  }
  const proj = selectionProjection.value.getProjection();
  const px = proj?.fromLatLngToContainerPixel(new google.maps.LatLng(lat, lng));
  emit('select-position', px ? { x: px.x, y: px.y } : null);
};
watch(() => props.selectedId, emitSelectedPinPosition);

const filterPropertiesByViewport = (properties: Property[]): Property[] => {
  if (!mapObject.value || !properties.length) return properties;

  const bounds = mapObject.value.getBounds();
  if (!bounds) return properties;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const minLat = sw.lat();
  const maxLat = ne.lat();
  const minLng = sw.lng();
  const maxLng = ne.lng();

  const filtered: Property[] = [];
  for (let i = 0; i < properties.length; i++) {
    const property = properties[i];
    const lat = Number(property.latitude);
    const lng = Number(property.longitude);
    if (lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng) {
      filtered.push(property);
    }
  }
  return filtered;
};

const createPropertyMarkers = () => {
  if (!mapObject.value || isUpdatingMarkers.value) return;

  const currentZoom = mapObject.value.getZoom() || 10;
  const shouldUseClustering = currentZoom < CLUSTERING_DISABLE_ZOOM;

  if (shouldUseClustering && !markerClusterer.value) return;

  if (isUpdatingMarkers.value) return;
  isUpdatingMarkers.value = true;

  requestAnimationFrame(() => {
    try {
      const bounds = mapObject.value?.getBounds();
      const visibleProperties = bounds ? filterPropertiesByViewport(props.properties || []) : props.properties || [];

      const currentPropertyIds = new Set<string>();
      const locationGroups = new Map<string, { properties: Property[]; indices: Map<string, number> }>();
      const propertyIdToLocationKey = new Map<string, string>();

      for (let i = 0; i < visibleProperties.length; i++) {
        const property = visibleProperties[i];
        const propertyId = property.complex_id || property.id;
        currentPropertyIds.add(propertyId);

        const lat = Number(property.latitude);
        const lng = Number(property.longitude);
        const locationKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
        propertyIdToLocationKey.set(propertyId, locationKey);

        if (!locationGroups.has(locationKey)) {
          locationGroups.set(locationKey, { properties: [], indices: new Map() });
        }
        const group = locationGroups.get(locationKey)!;
        group.indices.set(propertyId, group.properties.length);
        group.properties.push(property);
      }

      const markersToRemove: string[] = [];
      for (const id in propertyMarkers.value) {
        if (!currentPropertyIds.has(id)) {
          markersToRemove.push(id);
        }
      }

      for (let i = 0; i < markersToRemove.length; i++) {
        const id = markersToRemove[i];
        const marker = propertyMarkers.value[id];
        if (marker) {
          const clickListener = markerClickListeners.get(id);
          if (clickListener) {
            google.maps.event.removeListener(clickListener);
            markerClickListeners.delete(id);
          }
          marker.setMap(null);
          delete propertyMarkers.value[id];
        }
      }

      const exclusiveMarkers: google.maps.Marker[] = [];
      const nonExclusiveMarkers: google.maps.Marker[] = [];
      const isExclusiveFilterActive = props.filters?.exclusive === true;

      for (let i = 0; i < visibleProperties.length; i++) {
        const property = visibleProperties[i];
        const propertyId = property.complex_id || property.id;
        const existingMarker = propertyMarkers.value[propertyId];
        const isExclusive = property.isExclusive;

        if (existingMarker && currentPropertyIds.has(propertyId)) {
          if (isExclusive) {
            exclusiveMarkers.push(existingMarker);
          } else {
            nonExclusiveMarkers.push(existingMarker);
          }
        } else {
          let icon: string | google.maps.Icon = '/map-marker.svg';
          let zIndex = 1;

          if (isExclusive) {
            icon = {
              url: createExclusiveMarkerIcon(isExclusiveFilterActive),
              scaledSize: new google.maps.Size(32, 32),
              anchor: new google.maps.Point(16, 16),
            };
            zIndex = 2;
          } else {
            const statusLabel = property.sales_status?.en ?? (property.sales_status as { name?: string })?.name;
            const statusColor = getMarkerColorByStatus(statusLabel);
            icon = {
              url: createStatusMarkerIcon(statusColor),
              scaledSize: new google.maps.Size(28, 36),
              anchor: new google.maps.Point(14, 36),
            };
          }

          const lat = Number(property.latitude);
          const lng = Number(property.longitude);
          const locationKey = propertyIdToLocationKey.get(propertyId)!;
          const group = locationGroups.get(locationKey)!;
          const sameLocationIndex = group.indices.get(propertyId)!;

          let offsetLat = lat;
          let offsetLng = lng;

          if (group.properties.length > 1) {
            const offsetDistance = 0.0005;
            const totalMarkers = group.properties.length;
            const angleStep = (2 * Math.PI) / totalMarkers;
            const radius = offsetDistance;
            const angle = sameLocationIndex * angleStep;
            offsetLat = lat + Math.cos(angle) * radius;
            offsetLng = lng + Math.sin(angle) * radius;
          }

          const marker = new google.maps.Marker({
            position: {
              lat: offsetLat,
              lng: offsetLng,
            },
            icon,
            zIndex,
            title: (property.title as any)?.en || t('listings.map.propertyAlt'),
            clickable: true,
            optimized: false,
          });

          marker.set('propertyId', propertyId);
          propertyMarkers.value[propertyId] = marker;

          const clickListener = marker.addListener('click', () => {
            if (props.cardLinkMode) {
              emit('select-property', propertyId);
              return;
            }
            showPropertyPopup(property);
          });
          markerClickListeners.set(propertyId, clickListener);

          if (isExclusive) {
            exclusiveMarkers.push(marker);
          } else {
            nonExclusiveMarkers.push(marker);
          }
        }
      }

      const currentZoom = mapObject.value?.getZoom() || 10;
      const totalProperties = visibleProperties.length;
      const shouldUseClustering = currentZoom < CLUSTERING_DISABLE_ZOOM && totalProperties >= CLUSTERING_MIN_PROPERTIES;

      if (mapObject.value) {
        for (let i = 0; i < exclusiveMarkers.length; i++) {
          const marker = exclusiveMarkers[i];
          const currentMap = marker.getMap();
          if (currentMap !== mapObject.value) {
            marker.setMap(mapObject.value);
          }
        }
      }

      if (shouldUseClustering) {
        if (markerClusterer.value && nonExclusiveMarkers.length > 0) {
          markerClusterer.value.clearMarkers();
          markerClusterer.value.addMarkers(nonExclusiveMarkers);
        }
      } else {
        if (markerClusterer.value) {
          markerClusterer.value.clearMarkers();
        }

        if (mapObject.value) {
          for (let i = 0; i < nonExclusiveMarkers.length; i++) {
            const marker = nonExclusiveMarkers[i];
            const currentMap = marker.getMap();
            if (currentMap !== mapObject.value) {
              marker.setMap(mapObject.value);
            }
          }
        }
      }

      isUpdatingMarkers.value = false;
    } catch (error) {
      console.error('Error creating markers:', error);
      isUpdatingMarkers.value = false;
    }
  });
};

const createButton = (
  html: string,
  classes: string,
  ariaLabel: string,
  onClick: () => void,
  isActive?: boolean,
  onMouseOver?: () => void,
  onMouseOut?: () => void,
) => {
  const button = document.createElement('button');

  button.innerHTML = html;

  button.className = classes + (isActive ? 'bg-primary-90 text-white' : 'bg-white text-black-90');

  button.setAttribute('aria-label', ariaLabel);
  button.addEventListener('click', onClick);

  if (onMouseOver) {
    button.addEventListener('mouseover', onMouseOver);
  }

  if (onMouseOut) {
    button.addEventListener('mouseout', onMouseOut);
  }

  return button;
};

const controlsTypeMap = new Map([
  [
    'mapType',
    (map: google.maps.Map) => {
      const mapTypeControl = document.createElement('div');
      mapTypeControl.classList.add('flex', 'flex-row', 'mt-3', 'text-black-90');
      const mapButton = createButton(
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_4232_82188)"><path d="M2 5.95715L12 9.5L22 5.95715L12 2.5L2 5.95715Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M2 10L12 13.5L22 10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 14L12 17.5L22 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 18L12 21.5L22 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></g></svg>',
        'bg-white p-2 rounded-xl text-2xl h-10 flex justify-center shadow-lg',
        t('listings.map.controls.changeMapType'),
        () => {},
        false,
        () => {
          mapButton.classList.add('text-primary-90');
          mapButton.classList.remove('text-black-90');
          popover.classList.remove('hidden');
        },
        () => {
          setTimeout(() => {
            if (
              !(
                x.value > mapButton.getBoundingClientRect().left &&
                x.value < mapButton.getBoundingClientRect().right &&
                y.value > mapButton.getBoundingClientRect().top &&
                y.value < mapButton.getBoundingClientRect().bottom
              ) &&
              !(
                x.value > popover.getBoundingClientRect().left &&
                x.value < popover.getBoundingClientRect().right &&
                y.value > popover.getBoundingClientRect().top &&
                y.value < popover.getBoundingClientRect().bottom
              )
            ) {
              popover.classList.add('hidden');
              mapButton.classList.remove('text-primary-90');
              mapButton.classList.add('text-black-90');
            }
          }, 1000);
        },
      );

      const popover = document.createElement('div');
      popover.classList.add(
        'absolute',
        'start-14',
        'top-4',
        'w-[222px]',
        'h-[112px]',
        'bg-white',
        'shadow-lg',
        'rounded-lg',
        'hidden',
        'z-10',
        'flex',
        'flex-row',
        'gap-2',
        'p-4',
      );
      popover.innerHTML = `
  <div class="cursor-pointer w-1/2 text-center text-subhead-4 text-subhead-4" id="mapTypeRoadmap">
  <img id="mapTypeRoadmapImage" src="/roadmap.webp" class="w-full h-[56px] rounded-lg border border-solid border-black-100"/>
  ${escapeHtml(t('listings.map.controls.mapType'))}
  </div>
  <div class="cursor-pointer w-1/2 text-center text-body-2" id="mapTypeSatellite">
  <img id="mapTypeSatelliteImage" src="/satellite.webp" class="w-full h-[56px] rounded-lg border border-solid border-black-70"/>${escapeHtml(t('listings.map.controls.satelliteType'))}</div>
`;

      const roadmapImage = popover.querySelector('#mapTypeRoadmapImage');
      const satelliteImage = popover.querySelector('#mapTypeSatelliteImage');
      const mapTypeRoadmap = popover.querySelector('#mapTypeRoadmap');
      const mapTypeSatellite = popover.querySelector('#mapTypeSatellite');
      if (!roadmapImage || !satelliteImage || !mapTypeRoadmap || !mapTypeSatellite) return;
      mapTypeRoadmap.addEventListener('click', () => {
        map.setMapTypeId(google.maps.MapTypeId.ROADMAP);
        mapTypeRoadmap.classList.add('text-subhead-4');
        mapTypeRoadmap.classList.add('text-black-100');
        roadmapImage.classList.add('border-[1.5px]');
        roadmapImage.classList.add('border-black-100');
        mapTypeRoadmap.classList.remove('text-body-2');
        mapTypeRoadmap.classList.remove('text-black-60');
        roadmapImage.classList.remove('border');
        roadmapImage.classList.remove('border-black-70');

        mapTypeSatellite.classList.remove('text-subhead-4');
        mapTypeSatellite.classList.remove('text-black-100');
        satelliteImage.classList.remove('border-[1.5px]');
        satelliteImage.classList.remove('border-black-100');
        mapTypeSatellite.classList.add('text-body-2');
        mapTypeSatellite.classList.add('text-black-60');
        satelliteImage.classList.add('border');
        satelliteImage.classList.add('border-black-70');
        popover.classList.add('hidden');
      });

      mapTypeSatellite.addEventListener('click', () => {
        map.setMapTypeId(google.maps.MapTypeId.SATELLITE);
        mapTypeSatellite.classList.add('text-subhead-4');
        mapTypeSatellite.classList.add('text-black-100');
        satelliteImage.classList.add('border-[1.5px]');
        satelliteImage.classList.add('border-black-100');
        mapTypeSatellite.classList.remove('text-body-2');
        mapTypeSatellite.classList.remove('text-black-60');
        satelliteImage.classList.remove('border');
        satelliteImage.classList.remove('border-black-70');

        mapTypeRoadmap.classList.remove('text-subhead-4');
        mapTypeRoadmap.classList.remove('text-black-100');
        roadmapImage.classList.remove('border-[1.5px]');
        roadmapImage.classList.remove('border-black-100');
        mapTypeRoadmap.classList.add('text-body-2');
        roadmapImage.classList.add('text-black-60');
        roadmapImage.classList.add('border');
        roadmapImage.classList.add('border-black-70');

        popover.classList.add('hidden');
      });

      mapTypeControl.appendChild(mapButton);
      mapTypeControl.appendChild(popover);
      return mapTypeControl;
    },
  ],
  [
    'zoom',
    (map) => {
      const zoomControl = document.createElement('div');
      zoomControl.classList.add('flex', 'flex-col', 'rounded-xl', 'w-10');

      const zoomInButton = createButton(
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.75 5C12.75 4.58579 12.4142 4.25 12 4.25C11.5858 4.25 11.25 4.58579 11.25 5V11.25H5C4.58579 11.25 4.25 11.5858 4.25 12C4.25 12.4142 4.58579 12.75 5 12.75H11.25V19C11.25 19.4142 11.5858 19.75 12 19.75C12.4142 19.75 12.75 19.4142 12.75 19V12.75H19C19.4142 12.75 19.75 12.4142 19.75 12C19.75 11.5858 19.4142 11.25 19 11.25H12.75V5Z" fill="currentColor"/></svg>',
        'bg-white p-2 rounded-t-xl text-2xl h-10 flex justify-center mt-6 shadow-lg',
        t('listings.map.controls.zoomIn'),
        () => map.setZoom((map.getZoom() || 0) + 1),
      );
      const zoomOutButton = createButton(
        '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.25 12C4.25 11.5858 4.58579 11.25 5 11.25H19C19.4142 11.25 19.75 11.5858 19.75 12C19.75 12.4142 19.4142 12.75 19 12.75H5C4.58579 12.75 4.25 12.4142 4.25 12Z" fill="currentColor"/></svg>',
        'bg-white p-2 rounded-b-xl text-2xl flex justify-center mb-3 shadow-lg',
        t('listings.map.controls.zoomOut'),
        () => map.setZoom((map.getZoom() || 0) - 1),
      );
      zoomControl.appendChild(zoomInButton);
      zoomControl.appendChild(zoomOutButton);
      return zoomControl;
    },
  ],
  [
    'center',
    (map) => {
      const centerControl = document.createElement('div');
      centerControl.classList.add('flex', 'flex-col', 'rounded-xl', 'w-10');

      const button = createButton(
        '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_1812_17864)"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 7.63636C9.58909 7.63636 7.63636 9.58909 7.63636 12C7.63636 14.4109 9.58909 16.3636 12 16.3636C14.4109 16.3636 16.3636 14.4109 16.3636 12C16.3636 9.58909 14.4109 7.63636 12 7.63636ZM21.7527 10.9091C21.2509 6.36 17.64 2.74909 13.0909 2.24727V1.09091C13.0909 0.490909 12.6 0 12 0C11.4 0 10.9091 0.490909 10.9091 1.09091V2.24727C6.36 2.74909 2.74909 6.36 2.24727 10.9091H1.09091C0.490909 10.9091 0 11.4 0 12C0 12.6 0.490909 13.0909 1.09091 13.0909H2.24727C2.74909 17.64 6.36 21.2509 10.9091 21.7527V22.9091C10.9091 23.5091 11.4 24 12 24C12.6 24 13.0909 23.5091 13.0909 22.9091V21.7527C17.64 21.2509 21.2509 17.64 21.7527 13.0909H22.9091C23.5091 13.0909 24 12.6 24 12C24 11.4 23.5091 10.9091 22.9091 10.9091H21.7527ZM12 19.6364C7.77818 19.6364 4.36364 16.2218 4.36364 12C4.36364 7.77818 7.77818 4.36364 12 4.36364C16.2218 4.36364 19.6364 7.77818 19.6364 12C19.6364 16.2218 16.2218 19.6364 12 19.6364Z" fill="currentColor"/></g><defs><clipPath id="clip0_1812_17864"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>',
        'bg-white p-2 rounded-xl text-2xl h-10 flex justify-center shadow-lg',
        t('listings.map.controls.centerMap'),
        () => {
          map.setCenter(
            props.centerByMarker && props.properties.length > 0
              ? { lat: props.properties[0].latitude, lng: props.properties[0].longitude }
              : { lat: 25.2048, lng: 55.2708 },
          );
          map.fitBounds(
            new google.maps.LatLngBounds(
              new google.maps.LatLng(25.0948, 55.1608),
              new google.maps.LatLng(25.3148, 55.3808),
            ),
          );
        },
      );
      centerControl.appendChild(button);
      return centerControl;
    },
  ],
]);

const addControl = (map: any, type: 'center' | 'zoom' | 'mapType', position: number) => {
  const controlDiv = document.createElement('div');
  controlDiv.classList.add('flex', 'flex-col', 'rounded-xl', 'mx-3', 'md:mx-6');

  const button = controlsTypeMap.get(type)?.(map);

  if (!button) return;

  controlDiv.appendChild(button);
  map.controls[position].push(controlDiv);
};

const resetButtons = () => {};

import { formatPrice } from '~/helpers/currency.helper';

const authStore = useAuthStore();

const getSalesStatusDisplay = (status: string | undefined): string => {
  if (!status) return '';

  const normalizedStatus = status.toLowerCase().replace(/[_\s]/g, '_');
  const statusMap: Record<string, string> = {
    presale_eoi: 'Presale (EOI)',
    presale: 'Presale',
    ready: 'Ready',
    off_plan: 'Off Plan',
    sold_out: 'Sold Out',
    on_sale: 'On Sale',
    announcement: 'Announcement',
    launch: 'Launch',
    pending: 'Pending',
    'presale_(eoi)': 'Presale (EOI)',
  };

  return statusMap[normalizedStatus] || status;
};

const salesStatusText = computed(() => {
  if (!selectedProperty.value?.sales_status?.en) return 'Presale (EOI)';
  return getSalesStatusDisplay(selectedProperty.value.sales_status.en);
});

const showPropertyPopup = async (property: Property) => {
  isPopupLoading.value = true;

  try {
    await nextTick();
    selectedProperty.value = property;
    selectedPropertyPhotoUrl.value = property.photo || COMING_SOON_PLACEHOLDER;

    if (property.photo) {
      checkComingSoonImage(property.photo)
        .then((result) => {
          if (result && selectedProperty.value?.complex_id === property.complex_id) {
            selectedPropertyPhotoUrl.value = COMING_SOON_PLACEHOLDER;
          }
        })
        .catch(() => {});
    }

    await checkPropertyFavoriteStatus();
  } finally {
    isPopupLoading.value = false;
  }
};

const closePropertyPopup = () => {
  selectedProperty.value = null;
  selectedPropertyPhotoUrl.value = COMING_SOON_PLACEHOLDER;
  isPropertyFavorite.value = false;
  isPopupLoading.value = false;
};

const { sendEventToBackend } = useTracking();

const navigateToProperty = () => {
  if (!selectedProperty.value) return;
  const exclusive = selectedProperty.value.exclusive;
  if (exclusive?.isExclusive) {
    void sendEventToBackend('exclusive_project_opened', {
      catalogueProjectId: exclusive.catalogueProjectId,
      source: 'map',
    });
  }
  const route = useRoute();
  const isFromExplore = route.path.includes('/explore');
  // Prefer the SEO-friendly /projects/<dev>/<slug> URL when the marker
  // carries both slug fields (populated by mapToPropertyShape on the
  // server). Falls back to the legacy /properties/<id> only when the slug
  // pair is missing — the property-id-to-slug-redirect middleware will
  // still 301 those to the canonical URL.
  const p = selectedProperty.value as { complex_id: string; slug?: string; developerSlug?: string };
  const path = p.slug && p.developerSlug ? `/projects/${p.developerSlug}/${p.slug}` : `/properties/${p.complex_id}`;
  navigateTo({
    path,
    query: isFromExplore ? { from: 'explore' } : {},
  });
};

const { mutateAsync: addToFavorites } = useAddToFavorites();
const { mutateAsync: removeFromFavorites } = useRemoveFromFavorites();

const checkPropertyFavoriteStatus = async () => {
  if (!selectedProperty.value || !authStore.user?.id) {
    isPropertyFavorite.value = false;
    return;
  }

  try {
    const { data: favoriteStatus } = await useIsPropertyFavorited(
      selectedProperty.value.complex_id,
      authStore.user.id.toString(),
    );
    isPropertyFavorite.value = favoriteStatus.value || false;
  } catch (error) {
    isPropertyFavorite.value = false;
  }
};

const togglePropertyFavorite = async () => {
  if (!selectedProperty.value || !authStore.user?.id) {
    await navigateTo('/login');
    return;
  }

  if (isFavoriteLoading.value) return;

  isFavoriteLoading.value = true;
  const previousState = isPropertyFavorite.value;

  try {
    if (previousState) {
      await removeFromFavorites({
        complexId: selectedProperty.value.complex_id,
        userId: authStore.user.id.toString(),
      });
      isPropertyFavorite.value = false;
    } else {
      await addToFavorites({
        complex_id: selectedProperty.value.complex_id,
      });
      isPropertyFavorite.value = true;
    }
  } catch (error) {
    isPropertyFavorite.value = previousState;
  } finally {
    isFavoriteLoading.value = false;
  }
};

const getPropertyUnitsText = (apartments: Property['apartments']) => {
  const units = getPropertyUnits(apartments);
  return units.map((u: any) => u.title).join(', ');
};

const aedFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

// P6 en-branch: en keeps the exact original compact-number output; other
// locales get a locale-aware compact number via useFormat() so digit
// grouping/glyphs follow their convention (the " AED" suffix stays literal
// in the template — AED is a currency code, never translated).
const formattedMinPrice = computed(() => {
  if (!selectedProperty.value?.price?.min) return '0';
  const value = Number(selectedProperty.value.price.min);
  if (currentLocale.value === 'en') return aedFormatter.format(value);
  return formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 });
});

watch(
  [() => props.properties?.length, () => props.filters?.exclusive, () => props.filters?.sale_status, mapObject],
  ([newLength, newExclusive, newSaleStatus], [oldLength, oldExclusive, oldSaleStatus]) => {
    if (!mapObject.value) return;

    const lengthChanged = newLength !== lastPropertiesLength.value;
    const exclusiveChanged = newExclusive !== lastExclusiveFilter.value;
    const saleStatusChanged = JSON.stringify(newSaleStatus) !== JSON.stringify(oldSaleStatus);

    if (!lengthChanged && !exclusiveChanged && !saleStatusChanged) return;

    lastPropertiesLength.value = newLength || 0;
    lastExclusiveFilter.value = newExclusive;

    if (propertiesWatchTimeout) {
      clearTimeout(propertiesWatchTimeout);
    }

    propertiesWatchTimeout = setTimeout(() => {
      if (mapObject.value && (markerClusterer.value || props.properties?.length)) {
        createPropertyMarkers();
      }
      propertiesWatchTimeout = null;
    }, 300);
  },
);

let boundsWatchTimeout: NodeJS.Timeout | null = null;

watch(
  () => mapObject.value?.getBounds(),
  () => {
    if (!mapObject.value || !markerClusterer.value || !props.properties?.length) return;

    if (boundsWatchTimeout) {
      clearTimeout(boundsWatchTimeout);
    }

    boundsWatchTimeout = setTimeout(() => {
      createPropertyMarkers();
      boundsWatchTimeout = null;
    }, 200);
  },
);

const formattedUnits = computed(() => {
  if (!selectedProperty.value?.apartments?.length) return [];

  const units = getPropertyUnits(selectedProperty.value.apartments);
  const availableUnits = units.filter((unit: any) => unit.title && unit.title !== 'No units available');

  const shortRoomTypeMap: Record<string, string> = {
    Studio: t('common.priceTable.studio'),
    '1 bedroom': t('listings.unitLabel.oneBed'),
    '2 bedroom': t('listings.unitLabel.twoBed'),
    '3 bedroom': t('listings.unitLabel.threeBed'),
    '4 bedroom': t('listings.unitLabel.fourBed'),
    '5 bedroom': t('listings.unitLabel.fiveBed'),
    '6 bedroom': t('listings.unitLabel.sixBed'),
    '7 bedroom': t('listings.unitLabel.sevenBed'),
  };

  return availableUnits.map((unit: any) => {
    return {
      ...unit,
      shortRoomType: shortRoomTypeMap[unit.title] || unit.title,
    };
  });
});

const { x, y } = usePointer();

const extractPathFromSvg = (svgString: string, index: number = 0): string => {
  const pathMatch = svgString.match(/<path[^>]*>/g);
  return pathMatch?.[index] || '';
};

const exclusiveMarkerIconCache = {
  active: '',
  inactive: '',
};

const statusMarkerIconCache: Record<string, string> = {};
const DEFAULT_MARKER_RED = '#D32F2F';

const createStatusMarkerIcon = (color: string): string => {
  if (statusMarkerIconCache[color]) {
    return statusMarkerIconCache[color];
  }

  const svg = `<svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.268 21.732 0 14 0z" fill="${color}" stroke="white" stroke-width="2"/>
    <circle cx="14" cy="14" r="5" fill="white" fill-opacity="0.9"/>
  </svg>`;

  const iconUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  statusMarkerIconCache[color] = iconUrl;

  return iconUrl;
};

const getMarkerColorByStatus = (status: string | undefined): string => {
  if (!status) return DEFAULT_MARKER_RED;
  const trimmed = String(status).trim();
  if (nameToColorPropertyMap[trimmed]) return nameToColorPropertyMap[trimmed];
  const normalizedStatus = trimmed.toLowerCase().replace(/[\s]+/g, '_').replace(/[()]/g, '');
  const statusMap: Record<string, string> = {
    presale_eoi: nameToColorPropertyMap['Presale (EOI)'],
    presale: nameToColorPropertyMap['Presale (EOI)'],
    on_sale: nameToColorPropertyMap['On Sale'],
    announcement: nameToColorPropertyMap['Announcement'],
    launch: nameToColorPropertyMap['Launch'],
    pending: nameToColorPropertyMap['Pending'],
    presaleeoi: nameToColorPropertyMap['Presale (EOI)'],
    sold_out: nameToColorPropertyMap['Sold Out'],
  };
  return statusMap[normalizedStatus] || nameToColorPropertyMap[trimmed] || '#D32F2F';
};

const createExclusiveMarkerIcon = (isExclusiveFilterActive: boolean): string => {
  if (isExclusiveFilterActive && exclusiveMarkerIconCache.active) {
    return exclusiveMarkerIconCache.active;
  }

  if (!isExclusiveFilterActive && exclusiveMarkerIconCache.inactive) {
    return exclusiveMarkerIconCache.inactive;
  }

  const crownPath = extractPathFromSvg(crownBackgroundSvg, 0);

  const starColor = isExclusiveFilterActive ? '#000000' : '#FFFFFF';
  let starPath = extractPathFromSvg(starFilledSvg, 0);
  starPath = starPath.replace(/fill="[^"]*"/g, `fill="${starColor}"`);
  starPath = starPath.replace(/stroke="[^"]*"/g, `stroke="${starColor}"`);

  const svg = `<svg width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
    ${crownPath}
    <svg x="12.5" y="12.5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      ${starPath}
    </svg>
  </svg>`;

  const iconUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));

  if (isExclusiveFilterActive) {
    exclusiveMarkerIconCache.active = iconUrl;
  } else {
    exclusiveMarkerIconCache.inactive = iconUrl;
  }

  return iconUrl;
};

const getMarkerOptions = ({
  lat,
  lng,
  property,
}: google.maps.LatLngLiteral & {
  property?: Property;
}): google.maps.MarkerOptions => {
  let icon: string | google.maps.Icon;
  let zIndex = 1;

  if (property?.isExclusive === true) {
    const isExclusiveFilterActive = props.filters?.exclusive === true;
    icon = {
      url: createExclusiveMarkerIcon(isExclusiveFilterActive),
      scaledSize: new google.maps.Size(32, 32),
      anchor: new google.maps.Point(16, 16),
    };
    zIndex = 2;
  } else {
    const statusLabel = property?.sales_status?.en ?? (property?.sales_status as { name?: string })?.name;
    const color = property ? getMarkerColorByStatus(statusLabel) : '#3957D3';
    icon = {
      url: createStatusMarkerIcon(color),
      scaledSize: new google.maps.Size(28, 36),
      anchor: new google.maps.Point(14, 36),
    };
  }

  const options: google.maps.MarkerOptions = {
    icon,
    zIndex,
    position: { lat, lng },
    cursor: 'pointer',
    clickable: true,
  };
  if (mapObject.value) {
    options.map = mapObject.value;
  }
  return options;
};

const getPoiMarkerOptions = (poi: PointOfInterestMarker): google.maps.MarkerOptions => {
  const options: google.maps.MarkerOptions = {
    position: { lat: poi.lat, lng: poi.lng },
    icon: {
      url: createStatusMarkerIcon(DEFAULT_MARKER_RED),
      scaledSize: new google.maps.Size(28, 36),
      anchor: new google.maps.Point(14, 36),
    },
    zIndex: 0,
    title: poi.title,
    cursor: 'pointer',
    clickable: true,
  };
  if (mapObject.value) {
    options.map = mapObject.value;
  }
  return options;
};

onBeforeUnmount(() => {
  if (positionUpdateTimeout) {
    clearTimeout(positionUpdateTimeout);
    positionUpdateTimeout = null;
  }

  if (boundsWatchTimeout) {
    clearTimeout(boundsWatchTimeout);
    boundsWatchTimeout = null;
  }

  if (propertiesWatchTimeout) {
    clearTimeout(propertiesWatchTimeout);
    propertiesWatchTimeout = null;
  }

  eventListeners.forEach((listener) => {
    google.maps.event.removeListener(listener);
  });
  eventListeners.length = 0;

  containerClickListeners.forEach(({ element, listener }) => {
    element.removeEventListener('click', listener);
  });
  containerClickListeners.length = 0;

  if (hoverInfoWindow.value) {
    hoverInfoWindow.value.close();
    hoverInfoWindow.value = null;
  }

  markerClickListeners.forEach((listener, propertyId) => {
    google.maps.event.removeListener(listener);
  });
  markerClickListeners.clear();

  Object.values(propertyMarkers.value).forEach((marker) => {
    google.maps.event.clearInstanceListeners(marker);
    marker.setMap(null);
  });
  propertyMarkers.value = {};

  if (markerClusterer.value) {
    markerClusterer.value.clearMarkers();
    markerClusterer.value = null;
  }

  emiratesInfoWindow.value.forEach(({ infoWindow }) => {
    infoWindow.close();
  });
  emiratesInfoWindow.value = [];
});

defineExpose({ resetButtons });
</script>

<style scoped lang="scss">
.favorite-icon {
  width: 22px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.favorite-icon :deep(.icon) {
  width: 22px;
  height: 20px;
}

.favorite-icon :deep(svg) {
  width: 22px;
  height: 20px;
  display: block;
}

.favorite-active :deep(svg),
.favorite-active :deep(path) {
  fill: #ffb648;
  stroke: #ffb648;
}

.favorite-inactive :deep(svg),
.favorite-inactive :deep(path) {
  fill: white;
  stroke: white;
}

.view-project-button {
  width: 100%;
  margin-top: 12px;
  border-radius: 12px;
  background-color: #ffb648;
  color: rgb(17 17 17);
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  height: 42px;
  padding: 0 16px;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  transition: all 0.2s;
  font-family: 'Satoshi', sans-serif;
  letter-spacing: 0;
  font-style: normal;
}

.view-project-button:hover {
  filter: brightness(0.95);
}

.view-project-button:active {
  filter: brightness(0.9);
}

.view-project-button:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px rgb(255 182 72 / 0.5);
}

.view-project-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

// Physical `left` on purpose: the spinner lines up under the map's own controls, which live
// inside the `dir="ltr"`-pinned canvas and stay top-left in every direction.
.loader-position {
  left: 12px; // rtl-codemod-ignore: aligns with LTR-pinned map controls
  top: 13.7rem;
}

@media (min-width: 1024px) {
  .loader-position {
    left: 24px; // rtl-codemod-ignore: aligns with LTR-pinned map controls
  }
}
</style>
